import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { useLocation, useNavigate, UNSAFE_NavigationContext } from 'react-router-dom';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';

/**
 * A component that prompts the user before navigating away when there are unsaved changes.
 * Replacement for react-router-dom v5's Prompt component for v6/v7.
 * Works with BrowserRouter (doesn't require data router).
 * Intercepts: navigate() calls, <Link> clicks, browser back/forward, page refresh/close
 * 
 * @param {Object} props
 * @param {boolean} props.when - Whether to block navigation
 * @param {string} props.message - JSON string containing confirmText, messageText, cancelText
 */
const UnsavedChangesPrompt = ({ when, message }) => {
    const [showDialog, setShowDialog] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { navigator } = useContext(UNSAFE_NavigationContext);
    const isBlocking = useRef(false);
    const confirmedNavigation = useRef(false);

    // Parse the message JSON
    let messageConfig = {
        confirmText: "Yes, Leave this page",
        messageText: "You have unsaved changes that will be lost if you navigate away from this page. Are you sure you want to leave this page?",
        cancelText: "Stay on this Page",
    };

    try {
        if (message) {
            messageConfig = { ...messageConfig, ...JSON.parse(message) };
        }
    } catch (e) {
        // If message is not valid JSON, use it as messageText
        if (typeof message === 'string') {
            messageConfig.messageText = message;
        }
    }

    // Handle browser beforeunload event for page refresh/close
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (when) {
                e.preventDefault();
                e.returnValue = messageConfig.messageText;
                return messageConfig.messageText;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [when, messageConfig.messageText]);

    // Intercept programmatic navigation (navigate() calls and Link clicks)
    useEffect(() => {
        if (!when) return;

        const originalPush = navigator.push;
        const originalReplace = navigator.replace;

        navigator.push = (...args) => {
            if (confirmedNavigation.current) {
                confirmedNavigation.current = false;
                return originalPush.apply(navigator, args);
            }
            setPendingNavigation({ type: 'push', args, originalFn: originalPush });
            setShowDialog(true);
        };

        navigator.replace = (...args) => {
            if (confirmedNavigation.current) {
                confirmedNavigation.current = false;
                return originalReplace.apply(navigator, args);
            }
            setPendingNavigation({ type: 'replace', args, originalFn: originalReplace });
            setShowDialog(true);
        };

        return () => {
            navigator.push = originalPush;
            navigator.replace = originalReplace;
        };
    }, [when, navigator]);

    // Handle browser back/forward navigation
    useEffect(() => {
        if (!when) return;

        const handlePopState = (event) => {
            if (isBlocking.current || confirmedNavigation.current) return;
            
            // User tried to navigate back/forward - block it
            isBlocking.current = true;
            
            // Push the current location back onto the stack to cancel the navigation
            window.history.pushState(null, '', window.location.href);
            
            // Show confirmation dialog
            setPendingNavigation({ type: 'popstate', delta: -1 });
            setShowDialog(true);
            
            isBlocking.current = false;
        };

        // Push a state to enable popstate detection
        window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [when, location.pathname]);

    const handleCancel = useCallback(() => {
        setShowDialog(false);
        setPendingNavigation(null);
    }, []);

    const handleConfirm = useCallback(() => {
        setShowDialog(false);
        confirmedNavigation.current = true;
        
        if (pendingNavigation) {
            if (pendingNavigation.type === 'popstate') {
                // Allow the back navigation by going back
                window.history.go(pendingNavigation.delta);
            } else if (pendingNavigation.type === 'push' || pendingNavigation.type === 'replace') {
                // Re-execute the blocked navigation
                pendingNavigation.originalFn.apply(navigator, pendingNavigation.args);
            }
        }
        
        setPendingNavigation(null);
    }, [pendingNavigation, navigator]);

    if (!showDialog) {
        return null;
    }

    return (
        <Dialog
            open={true}
            onClose={handleCancel}
            aria-labelledby="unsaved-changes-dialog-title"
            aria-describedby="unsaved-changes-dialog-description"
        >
            <DialogTitle id="unsaved-changes-dialog-title">
                Unsaved Changes
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="unsaved-changes-dialog-description">
                    {messageConfig.messageText}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} color="primary">
                    {messageConfig.cancelText}
                </Button>
                <Button onClick={handleConfirm} color="secondary" autoFocus>
                    {messageConfig.confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UnsavedChangesPrompt;
